import { repairImageKitUrl } from '@dupe/common/constants'
import { ArrowRight, ChevronRight, ImageOff, Sparkle } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import { toBuyLinkHref } from '~/src/lib/withRedirectClickSource'

interface NotableMentionBuyLink {
  label: string
  url: string
  price?: number | null
}

interface NotableMention {
  slug: string
  name: string
  image?: string | null
  summary: string
  matchScore?: number | null
  pros?: string[]
  buyLink?: NotableMentionBuyLink | null
  pricing?: {
    currency?: string | null
  } | null
}

function formatPrice(
  price: number | null | undefined,
  currency?: string | null
): string {
  if (price == null) return ''
  const rounded = Math.round(price)
  const code = currency ?? 'USD'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(rounded)
  } catch {
    return `$${rounded}`
  }
}

interface SeoReportNotableMentionsProps {
  title?: string
  seeAllLabel?: string
  seeAllHref?: string
  mentions: NotableMention[]
  /** When set, the Learn more action opens the product drawer. */
  onViewProduct?: (slug: string) => void
}

function canUseNextImage(url: string): boolean {
  if (url.startsWith('/')) return true
  try {
    const hostname = new URL(url).hostname
    return hostname === 'ik.imagekit.io' || hostname === 'assets.dupe.com'
  } catch {
    return false
  }
}

function ImagePlaceholder({ className }: { className?: string }) {
  return (
    <div
      className={
        className ??
        'flex aspect-square w-full items-center justify-center rounded-2xl border border-border bg-accent'
      }
    >
      <ImageOff className='size-8 text-foreground-muted' strokeWidth={1.5} />
    </div>
  )
}

function ProductImage({
  src,
  alt,
}: {
  src: string | null | undefined
  alt: string
}) {
  const [hasError, setHasError] = useState(false)

  if (!src || hasError) {
    return <ImagePlaceholder />
  }

  if (canUseNextImage(repairImageKitUrl(src))) {
    return (
      <div className='relative aspect-square w-full overflow-hidden rounded-2xl border border-border'>
        <Image
          src={repairImageKitUrl(src)}
          alt={alt}
          fill
          sizes='(max-width: 768px) 100vw, 33vw'
          className='object-cover'
          loading='lazy'
          onError={() => setHasError(true)}
        />
      </div>
    )
  }

  return (
    <div className='relative aspect-square w-full overflow-hidden rounded-2xl border border-border'>
      <img
        src={repairImageKitUrl(src)}
        alt={alt}
        className='h-full w-full object-cover'
        loading='lazy'
        onError={() => setHasError(true)}
      />
    </div>
  )
}

function MobileMentionImage({
  image,
  name,
}: {
  image?: string | null
  name: string
}) {
  const [hasError, setHasError] = useState(false)

  if (!image || hasError) {
    return (
      <ImagePlaceholder className='flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-accent md:hidden' />
    )
  }

  const img = canUseNextImage(image) ? (
    <Image
      src={repairImageKitUrl(image)}
      alt={name}
      width={72}
      height={72}
      className='h-full w-full object-cover'
      loading='lazy'
      onError={() => setHasError(true)}
    />
  ) : (
    <img
      src={repairImageKitUrl(image)}
      alt={name}
      className='h-full w-full object-cover'
      loading='lazy'
      onError={() => setHasError(true)}
    />
  )

  return (
    <div className='h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl border border-border md:hidden'>
      {img}
    </div>
  )
}

export function SeoReportNotableMentions({
  title = 'Notable mentions',
  seeAllLabel,
  seeAllHref,
  mentions,
  onViewProduct,
}: SeoReportNotableMentionsProps) {
  if (mentions.length === 0) return null

  return (
    <section className='flex flex-col gap-6'>
      <div className='flex items-baseline justify-between'>
        <h2 className='font-display text-2xl font-bold text-foreground'>
          {title}
        </h2>
        {seeAllLabel && seeAllHref && (
          <a
            href={seeAllHref}
            className='inline-flex items-center gap-1 text-base font-semibold text-info-700'
          >
            {seeAllLabel}
            <ChevronRight className='size-4' />
          </a>
        )}
      </div>

      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3'>
        {mentions.map((mention) => {
          const canViewProduct = Boolean(onViewProduct)

          return (
            <article
              key={mention.slug}
              className='flex flex-row items-center gap-3 md:flex-col md:items-stretch md:justify-between md:gap-4'
            >
              {/* Mobile: small 72x72 image; Desktop: full-width image */}
              <MobileMentionImage image={mention.image} name={mention.name} />
              <div className='hidden md:block'>
                <ProductImage src={mention.image} alt={mention.name} />
              </div>
              <div className='flex min-w-0 flex-1 flex-col gap-2'>
                <h3 className='line-clamp-2 text-balance text-base font-semibold text-foreground'>
                  {mention.name}
                </h3>

                {mention.summary && (
                  <p className='line-clamp-3 text-sm leading-5 text-foreground-secondary'>
                    {mention.summary}
                  </p>
                )}

                {mention.pros && mention.pros.length > 0 && (
                  <ul className='flex flex-col gap-1' role='list'>
                    {mention.pros.map((pro, i) => (
                      <li key={`pro-${i}`} className='flex items-start gap-1.5'>
                        <Sparkle
                          className='mt-1 size-3 shrink-0 text-info-500'
                          fill='currentColor'
                          strokeWidth={0}
                        />
                        <span className='text-xs leading-4 text-foreground-secondary'>
                          {pro}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {mention.buyLink && (
                  <a
                    href={toBuyLinkHref(mention.buyLink.url, 'seo-report')}
                    target='_blank'
                    rel='sponsored nofollow noopener noreferrer'
                    className='inline-flex items-center gap-1 self-start text-sm font-semibold text-info-700 hover:underline'
                  >
                    {mention.buyLink.label}
                    {mention.buyLink.price != null && (
                      <span>
                        {' — '}
                        {formatPrice(
                          mention.buyLink.price,
                          mention.pricing?.currency
                        )}
                      </span>
                    )}
                  </a>
                )}

                {canViewProduct ? (
                  <button
                    type='button'
                    onClick={() => {
                      if (onViewProduct) onViewProduct(mention.slug)
                    }}
                    className='inline-flex items-center gap-1 self-start text-sm font-semibold text-info-700'
                  >
                    Learn more{' '}
                    <ArrowRight className='size-4' aria-hidden='true' />
                  </button>
                ) : (
                  <a
                    href={`#${mention.slug}`}
                    className='inline-flex items-center gap-1 text-sm font-semibold text-info-700'
                  >
                    Learn more{' '}
                    <ArrowRight className='size-4' aria-hidden='true' />
                  </a>
                )}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}
